import React, { useEffect, useRef, useState } from 'react';
import './ReportsNative.css';

/**
 * Wraps a report page's data content with the refetch motion language:
 *
 *  - while `fetching`, the region veils (gentle opacity pulse + orange
 *    shimmer sweep, .rn-refetching) so it is obvious work is happening
 *    behind the stale numbers (keepPreviousData UX);
 *  - the moment fresh data lands, a one-shot settle animation
 *    (.rn-settling) lifts the content back to full strength while the
 *    charts/numerals tween to their new values.
 *
 * Scope each region to the visuals a given query actually feeds: wrap
 * the page's main `{data && (...)}` block with the main query's flag,
 * and wrap secondary-query visuals (cross-filter chart queries, lazy
 * drills) in their own nested regions keyed to that query — nested
 * veils don't compound (see the .rn-refetching .rn-refetching CSS
 * guard), and visuals that aren't waiting on data never get obscured.
 */
export function RefetchRegion({ fetching, className, children }) {
  const [settling, setSettling] = useState(false);
  const wasFetching = useRef(false);

  useEffect(() => {
    const was = wasFetching.current;
    wasFetching.current = Boolean(fetching);
    if (was && !fetching) {
      setSettling(true);
      const t = setTimeout(() => setSettling(false), 650);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [fetching]);

  const cls = [
    'rn-refetch-region',
    fetching ? 'rn-refetching' : '',
    !fetching && settling ? 'rn-settling' : '',
    className || '',
  ]
    .filter(Boolean)
    .join(' ');

  return <div className={cls}>{children}</div>;
}

export default RefetchRegion;
